/**
 * @author Hadeer Elbanna
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2019-07-25 03:10
 * @description Implementation of the Matrix Panel.
 * @filename  matrix-panel.tsx
 */
import { ListItem as GraphQlListItem, Person, PreviewKind, TopLevelMediaKind } from "@radivision/graphql";
import React from "react";
import LazyLoad from "react-lazyload";
import ProgressiveImage from "react-progressive-image";
import { graphql, QueryRenderer } from "react-relay";
import { OptimizedImageUrls } from "../../component-configuration/optimized-image";
import { ENVIRONMENT } from "../../relay/relay-environment";
import { ImageHelper } from "../../utilities/image-helper";
import { Router } from "../../utilities/router";
import company1 from "../../../static/companies/Gilead Sciences logo for black background.png";
import companyMicrosoft from "../../../static/companies/companyMicrosoft.png";
import companyAmazon from "../../../static/companies/companyAmazonW.png";
import whiteHouse from "../../../static/companies/the_white_house.png";
import security from "../../../static/companies/securities_commission.png";
import company3 from "../../../static/companies/Truebill-Logo.png";
import company4 from "../../../static/companies/new-york-times-logo.png";
import company5 from "../../../static/companies/envestnet-logo.png";
import company6 from "../../../static/companies/sony-logo.png";
import company7 from "../../../static/companies/trinet-logo.png";
import company8 from "../../../static/companies/apple-logo.png";
import company9 from "../../../static/companies/goldman-sachs-logo.png";
import company10 from "../../../static/companies/disney-logo.png";
import company13 from "../../../static/companies/e-entertainment-logo.png";
import { AUTHENTICATION_MODAL_ID, AUTH_SIGNUP } from "../../redux/authentication/constants";
import { store, AppState } from "../../redux";
import { setAuthState } from "../../redux/actions";
import { connect } from "react-redux";

/**
 * The props of the MatrixPanel component.
 *
 * @interface
 */
interface MatrixPanelProps {
  json: {
    /**
     * the title of the Panel
     *
     * @type {string}
     */
    ListId: string;

    /**
     * number of items in the row
     *
     * @type {number}
     */
    listItemRow: number;

    /**
     *
     *
     * @type {boolean}
     */
    enableOnClick?: boolean;

    /**
     *
     *
     * @type {number}
     */
    founders?: number;

    showIcons?: boolean;
  };
}

const FOUNDERS_COUNT = 6;

/**
 *  A React component that renders the Matrix Panel.
 *
 * @export
 * @class  Matrix
 * @extends {React.Component< MatrixPanelProps>}
 */
export class MatrixPanel extends React.Component<MatrixPanelProps, {}> {
  /**
   * Constructor.
   *
   * @param { MatrixPanelProps} props The props of the component.
   */
  constructor(props: MatrixPanelProps) {
    super(props);
    this.state = {};
  }

  /**
   * A function triggered to handle images  that are not found
   *
   * @type {function()}
   */
  loadDefaultImage(targetImage: any, itemType: any) {
    targetImage.onerror = null;
    const defaultImage = ImageHelper.fetchOptimizedImageUrl({
      imageType: "DEFAULT",
      default: {
        previewType: PreviewKind.SQUARE,
        mediaType: TopLevelMediaKind.IMAGE,
        type: itemType,
      },
      desiredDimensions: { containerWidthRatio: 1, numberOfItems: 5 },
      isSquareImage: true,
      revision: undefined,
    });
    targetImage.src = defaultImage?.requestedResolutionUrl || "";
  }

  /**
   * Returns a ReactNode containing the rendered component.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered component.
   */
  render(): React.ReactNode {
    const listId: string = this.props.json.ListId;
    const FOUNDERS: number = this.props.json.founders ? this.props.json.founders : FOUNDERS_COUNT;

    if (listId === undefined || listId === null) {
      return <div className="alert alert-danger about-danger">Error Loading Matrix!</div>;
    }
    // ... should handle if not id provided from backend so it raise an error
    return (
      <QueryRenderer
        environment={ENVIRONMENT}
        query={graphql`
          query matrixPanelQuery($id: ID!) {
            list(id: $id) {
              hasErrors
              list {
                ... on FixedList {
                  id
                  title
                  name
                  description
                  items {
                    count
                    edges {
                      __typename
                      listItem {
                        item {
                          __typename
                          ... on Person {
                            id
                            fullName
                            landingPageUrl
                            socialMediaProfiles {
                              id
                              link
                              platform {
                                id
                                kind
                              }
                            }
                            previews {
                              edges {
                                preview {
                                  mediaAsset {
                                    files {
                                      id
                                      name
                                      path
                                    }
                                  }
                                  kind
                                }
                              }
                            }
                            responsibility
                            description
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `}
        variables={{ id: listId }}
        render={({ error, props }) => {
          if (error) {
            return <div>{error.message}</div>;
          }
          if (props) {
            if (props.list.hasErrors) {
              return (
                <div>
                  <p>unexpected error : {JSON.stringify(props.errors)}, contact Radivision Technical team.</p>{" "}
                </div>
              );
            }
            if (props.list.list) {
              // let numberOfItems: any = props.list.list.items.count / 6 <= 2;
              Router.getLocation().then((location: string) => {
                if (location === "team") {
                  setTimeout(() => {
                    Router.scrollTo(`:${location}`);
                  }, 100);
                }
              });
              const isTeamMembers: boolean = props.list.list.name && props.list.list.name.toLowerCase() === "team";

              const isAdvisors: boolean = props.list.list.name && props.list.list.name.toLowerCase() === "advisors";

              const isEmeritus: boolean = this.props?.json?.showIcons;
              const members =
                props.list.list.items && props.list.list.items.edges && props.list.list.items.edges.length
                  ? props.list.list.items.edges.slice()
                  : [];

              const founders = this.props.json.founders || FOUNDERS;
              const listItemRow = this.props.json.listItemRow || 5;

              const foundersList = isTeamMembers && members.length ? members.splice(0, founders) : [];
              const sortedMembers = members;
              const remainingMembers = sortedMembers;

              const rows = remainingMembers.length
                ? Array.from({ length: listItemRow }, (_, index) => remainingMembers.splice(0, listItemRow))
                : [];

              const rowStyle = !isTeamMembers ? { marginTop: "4rem" } : {};
              return (
                <>
                  <div id=":team" className="team container-fluid">
                    <h1 className="mission-title">{props.list.list.title}</h1>
                    {isAdvisors && (
                      <div className="advisor-intro">
                        <p>Fintech, Investment, Tech, Media, Legislative, Content, & Strategy Experts </p>
                      </div>
                    )}
                    {isTeamMembers && foundersList.length > 0 && (
                      <div className="row">
                        <div className="col-12 col-md-12 col-lg-12 advisor">
                          {foundersList.map((edge, index) => this.renderList(edge.listItem, index, isTeamMembers))}
                        </div>
                      </div>
                    )}


                    {rows.map((row, rowIndex) => (
                      <div className="row" key={rowIndex}>
                        <div className="col-12 col-md-12 col-lg-12 advisor">
                          {row.map((edge, index) => this.renderList(edge.listItem, index + founders, isTeamMembers))}
                        </div>
                      </div>
                    ))}

                    <div className="row" style={rowStyle}>


                      {/* {!isTeamMembers && (
                        <div className="col-12 col-md-12 col-lg-12 advisor">
                          {sortedMembers.map((edge: any, index: number) => {
                            if (
                              edge.listItem &&
                              edge.listItem.item &&
                              edge.listItem.item.fullName !== null &&
                              edge.listItem.item.fullName !== undefined
                            ) {
                              return this.renderList(edge.listItem, index + FOUNDERS, isTeamMembers);
                            }
                          })}
                        </div>
                      )} */}
                      {isEmeritus && (
                        <div className="advisor-intro">
                          <ul className="group1">
                            <li>
                              <img src={company10} id="disney" />
                            </li>
                            <li>
                              <img src={company8} id="apple" />
                            </li>
                            <li>
                              <img src={company13} id="e" />
                            </li>
                            <li>
                              <img src={company6} id="sony" />
                            </li>
                            <li>
                              <img src={company7} id="trinet" />
                            </li>
                            <li>
                              <img src={companyAmazon} id="amazon" />
                            </li>
                            <li>
                              <img src={companyMicrosoft} id="microsoft" />
                            </li>
                            <li>
                              <img src={company9} id="goldman-sacks" />
                            </li>
                          </ul>
                          <ul className="group2">
                            <li>
                              <img src={company1} id="gilead" />
                            </li>

                            <li>
                              <img src={company3} id="truebill" />
                            </li>
                            <li>
                              <img src={company4} id="f" />
                            </li>
                            <li>
                              <img src={company5} id="envestnet" />
                            </li>
                            <li>
                              <img src={whiteHouse} id="whiteHouse" />
                            </li>
                            <li>
                              <img src={security} id="security" />
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* {isTeamMembers && <JoinBanner />} */}
                </>
              );
            }
          }
          return <div>Loading</div>;
        }}
      />
    );
  }

  /**
   * Returns a ReactNode containing a rendered Matrix Panel item.
   *
   * @param {GraphQlListItem} listItem The item to be rendered.
   *
   * @param {string} key A key for the item.
   *
   * @param {string} itemClass Additional style information.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered Matrix Panel item.
   */
  renderList(listItem: GraphQlListItem, index: number, isTeamMembers: boolean): React.ReactNode {
    // ... should handle if item has no previews it render default one
    const item: Person = listItem.item as Person;

    if (!item.fullName) return null;
    let itemPreview: OptimizedImageUrls | undefined;
    const windowSize = window.innerWidth;
    const defaultPreview: OptimizedImageUrls | undefined = ImageHelper.fetchOptimizedImageUrl({
      imageType: "DEFAULT",
      default: {
        previewType: PreviewKind.SQUARE,
        mediaType: TopLevelMediaKind.IMAGE,
        type: item.__typename,
      },
      desiredDimensions: {
        containerWidthRatio: windowSize > 460 ? 1 : 2,
        numberOfItems: 4,
      },
      isSquareImage: true,
      revision: undefined,
    });
    if (item !== null && item !== undefined) {
      if (item.previews) {
        itemPreview = ImageHelper.fetchOptimizedImageUrl({
          imageType: "PREVIEW",
          preview: {
            content: item.previews,
            previewKind: [PreviewKind.SQUARE, PreviewKind.HEADSHOT],
            topLevelMedia: TopLevelMediaKind.IMAGE,
          },
          desiredDimensions: {
            containerWidthRatio: windowSize > 460 ? 1 : 2,
            numberOfItems: 4,
          },
          isSquareImage: true,
          revision: item.revision,
        });
      }

      const isAdvisor = !isTeamMembers;

      // Ensure we have valid preview URLs before accessing requestedResolutionUrl
      const imageUrl = (itemPreview?.requestedResolutionUrl) || (defaultPreview?.requestedResolutionUrl) || "";
      const placeholderUrl = (itemPreview?.placeHolderUrl) || (defaultPreview?.placeHolderUrl) || "";

      return (
        <div
          // className="col-lg-2 col-sm-12 col-md-3"
          className={`fullWidth ${this.props.json.listItemRow === 7
            ? "col-lg-2 col-sm-12 col-md-5"
            : this.props.json.listItemRow === 6
              ? "col-lg-2 col-sm-12 col-md-4"
              : this.props.json.listItemRow === 5
                ? "col-lg-2 col-sm-12 col-md-4"
                : this.props.json.listItemRow === 4
                  ? "col-lg-3 col-sm-12 col-md-4"
                  : "col-lg-4 col-sm-12 col-md-3"
            }`}
          style={{ maxWidth: "300px" }}
          key={item.id}
        >
          {item !== null && item !== undefined ? (
            <div>
              <LazyLoad once={true}>
                <ProgressiveImage
                  src={imageUrl}
                  placeholder={placeholderUrl}
                >
                  {(src: any) => (
                    <img
                      loading="lazy"
                      src={src}
                      onError={(event) => {
                        this.loadDefaultImage(event.target, item.__typename);
                      }}
                      style={{
                        width: "68%",
                        display: "block",
                        margin: "0 auto",
                      }}
                      className={`profileImage${
                        // To Do change this to a define attributes mapped to provided props
                        this.props.json?.enableOnClick && !index ? " clickable" : ""
                        }`}
                      onClick={
                        this.props.json?.enableOnClick && !index
                          ? () => {
                            if (item?.landingPageUrl) {
                              Router.generateRouterData(item.landingPageUrl).then((data) => {
                                return Router.to(data.alias, data.qp);
                              });
                            }
                          }
                          : null
                      }
                    />
                  )}
                </ProgressiveImage>
              </LazyLoad>
              {item.fullName ? <figcaption className="name">{item.fullName}</figcaption> : null}
              {item.responsibility ? (
                <figcaption
                  className="title"
                  style={item.responsibility === 'Board Advisor' ? { color: '#4cb4e7' } : {}}
                >
                  <span>{item.responsibility}</span>
                </figcaption>
              ) : null}
              {isAdvisor && (
                <figcaption className="title second-line">
                  <span>{item?.description}</span>
                </figcaption>
              )}
            </div>
          ) : null}
        </div>
      );
    }
    return null;
  }
}

const JoinBanner = connect((state: AppState) => ({
  user: state.account.user,
}))(({ user }) => {
  if (user) return null;
  return (
    <div id="join-banner">
      <div className="gradient1"></div>
      <div className="gradient2"></div>
      <div className="gradient3"></div>
      <h2>The Entrepreneurial Revolution</h2>
      <button
        className="join-button"
        data-toggle="modal"
        data-target={`#${AUTHENTICATION_MODAL_ID}`}
        onClick={() => {
          store.dispatch(setAuthState({ step: AUTH_SIGNUP }));
        }}
      >
        Join Now
      </button>
    </div>
  );
});
